/**
 * UTM / referrer attribution capture.
 *
 * Persists ad-driven session metadata to localStorage so it survives the
 * landing → signup → subscribe gap. Last-touch wins when new UTMs arrive;
 * empty querystrings preserve the prior attribution. Expires after
 * `EXPIRY_DAYS` days so stale clicks don't get credited to later signups.
 *
 * Stripe's `subscription_data.metadata` accepts string values only and is
 * capped at 50 keys / 500 chars per value — we send 7 short strings.
 */

const STORAGE_KEY = "viz:attribution";
const EXPIRY_DAYS = 30;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
const MAX_VALUE_LEN = 200;

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_path?: string;
  captured_at: number;
}

export interface CaptureOptions {
  now?: number;
  search?: string;
  pathname?: string;
  referrer?: string;
}

export interface ReadOptions {
  now?: number;
}

function safeGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function safeSet(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Quota exceeded or storage disabled — silent fallback.
  }
}

function safeRemove(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignored
  }
}

function clip(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_VALUE_LEN);
}

/**
 * Capture attribution from the current location. Returns the persisted
 * record, or `null` when there's nothing to persist or read. Safe to call on
 * every landing — when no UTM keys are present, the prior attribution is
 * preserved unchanged.
 */
export function captureAttribution(opts: CaptureOptions = {}): Attribution | null {
  const now = opts.now ?? Date.now();
  const search = opts.search ?? (typeof window !== "undefined" ? window.location.search : "");
  const pathname = opts.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const referrer = opts.referrer ?? (typeof document !== "undefined" ? document.referrer : "");

  const params = new URLSearchParams(search);
  const utmValues: Partial<Record<UtmKey, string>> = {};
  let hasUtm = false;
  for (const key of UTM_KEYS) {
    const value = clip(params.get(key));
    if (value) {
      utmValues[key] = value;
      hasUtm = true;
    }
  }

  if (!hasUtm) {
    return getAttribution({ now });
  }

  const next: Attribution = {
    ...utmValues,
    captured_at: now,
    landing_path: clip(pathname) ?? "/",
  };
  const ref = clip(referrer);
  if (ref) next.referrer = ref;

  safeSet(JSON.stringify(next));
  return next;
}

/**
 * Read the persisted attribution. Returns `null` past the expiry window or
 * when storage is unreadable / empty.
 */
export function getAttribution(opts: ReadOptions = {}): Attribution | null {
  const now = opts.now ?? Date.now();
  const raw = safeGet();
  if (!raw) return null;
  let parsed: Attribution;
  try {
    parsed = JSON.parse(raw) as Attribution;
  } catch {
    safeRemove();
    return null;
  }
  if (!parsed || typeof parsed.captured_at !== "number") {
    safeRemove();
    return null;
  }
  if (now - parsed.captured_at > EXPIRY_MS) {
    safeRemove();
    return null;
  }
  return parsed;
}

export function clearAttribution(): void {
  safeRemove();
}

/**
 * Flatten attribution into a string-only record suitable for Stripe metadata
 * or Supabase user_metadata. `captured_at` is dropped — the timestamp lives
 * on the surrounding record (e.g., subscription.created).
 */
export function attributionPayload(
  attribution: Attribution | null | undefined,
): Record<string, string> {
  if (!attribution) return {};
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = attribution[key];
    if (value) out[key] = value;
  }
  if (attribution.referrer) out.referrer = attribution.referrer;
  if (attribution.landing_path) out.landing_path = attribution.landing_path;
  return out;
}

export function hasAnyUtm(attribution: Attribution | null | undefined): boolean {
  if (!attribution) return false;
  return UTM_KEYS.some((key) => Boolean(attribution[key]));
}
